/**
 * Jyotish Tarang — Delete Account (public page, no auth required)
 * Added 13 Aug 2026, alongside PrivacyPage.tsx: Google Play Console's Data
 * Safety form requires a live, publicly reachable "Delete account URL".
 * Account deletion itself is a mobile-app-only feature today (see
 * mobile/src/screens/ProfileScreen.tsx) — the web frontend has no deletion
 * UI — so this page documents the real in-app flow plus an email fallback
 * for anyone without app access, rather than implying a web flow exists.
 * Retention details mirror backend/api/auth.py's delete_account() and the
 * 180-day activity-log retention added the same day in
 * cron_cleanup_activity.py.
 */
import React from 'react';

const wrap: React.CSSProperties = {
  maxWidth: 760, margin: '0 auto', padding: '48px 24px 96px',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  color: '#2b2b2b', lineHeight: 1.65,
};
const h1: React.CSSProperties = { fontSize: 28, marginBottom: 4 };
const updated: React.CSSProperties = { color: '#777', fontSize: 14, marginBottom: 32 };
const h2: React.CSSProperties = { fontSize: 19, marginTop: 36, marginBottom: 10 };
const backLink: React.CSSProperties = { display: 'inline-block', marginBottom: 24, color: '#5b3fa0', textDecoration: 'none', fontSize: 14 };
const ol: React.CSSProperties = { paddingLeft: 22 };
const li: React.CSSProperties = { marginBottom: 8 };
const box: React.CSSProperties = { background: '#f7f5fb', border: '1px solid #e3ddf0', borderRadius: 8, padding: '16px 20px', margin: '16px 0' };

export default function DeleteAccountPage() {
  return (
    <div style={wrap}>
      <a href="/" style={backLink}>&larr; Jyotish Tarang</a>
      <h1 style={h1}>Delete Your Account</h1>
      <div style={updated}>Last updated: 13 August 2026</div>

      <p>
        This page explains how to permanently delete your Jyotish Tarang account (published on
        Google Play as <strong>Jyotishacharya: Vedic Astro</strong>) and what happens to your
        data when you do.
      </p>

      <h2 style={h2}>Option 1: Delete it yourself, in the app</h2>
      <ol style={ol}>
        <li style={li}>Open the Jyotish Tarang app and sign in.</li>
        <li style={li}>Go to <strong>Profile</strong>.</li>
        <li style={li}>Scroll down to the <strong>Danger zone</strong> section.</li>
        <li style={li}>Tap <strong>Delete my account</strong>.</li>
        <li style={li}>Confirm by tapping <strong>Delete account</strong>, then confirm again by tapping <strong>Yes, delete permanently</strong>.</li>
      </ol>
      <p>Your account is deleted immediately — there is no waiting period.</p>

      <h2 style={h2}>Option 2: Email us</h2>
      <p>
        If you don't have access to the app, email{' '}
        <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a> from
        the address registered on your account and ask us to delete it. We'll confirm your
        identity and process the deletion.
      </p>

      <h2 style={h2}>What gets deleted, and what's kept</h2>
      <div style={box}>
        <p style={{ marginTop: 0 }}><strong>Deleted immediately:</strong> your birth charts, AI chat/question history, and notification preferences.</p>
        <p><strong>Anonymized immediately:</strong> your name and email on our account record (the row is kept only so financial records below still resolve — it no longer identifies you).</p>
        <p style={{ marginBottom: 0 }}><strong>Retained:</strong> payment and subscription records, kept as required for financial and tax recordkeeping. A record of account activity (that an action happened and when — not its content) is kept for up to <strong>180 days</strong> after deletion to allow us to respond to legitimate compliance or government audit requests, then permanently purged.</p>
      </div>

      <p>
        See our full <a href="/privacy">Privacy Policy</a> for more detail on what we collect and why.
      </p>

      <h2 style={h2}>Questions</h2>
      <p>
        Email <a href="mailto:support@jyotish.vivekkhare.org">support@jyotish.vivekkhare.org</a> and we'll help.
      </p>
    </div>
  );
}
